import React, { useState, useContext, createContext, Dispatch, RefObject, SetStateAction, useEffect } from "react";
import { Workflow } from "./interfaces";
import { TemplatedTextEditorRef } from "./components/TemplatedTextEditor";
import { KeyedMutator } from "swr";
import { useLocation } from "react-router-dom";

// Modal

interface ModalContextData {
  openModal: (comp: JSX.Element) => void;
  closeModal: (onClose?: () => void) => void;
}

export const ModalContext = React.createContext<ModalContextData>({
  openModal: () => { },
  closeModal: () => { }
});

export function ModalProvider(props: { children: React.ReactNode }) {
  const [modal, setModal] = useState<JSX.Element | null>(null);

  const openModal = (el: JSX.Element) => setModal(el);
  const closeModal = (onClose?: () => void) => { setModal(null); if (onClose) onClose(); };

  return (
    <ModalContext.Provider value={{ openModal, closeModal }}>
      <div className="relative">
        {props.children}
        {modal}
      </div>
    </ModalContext.Provider>
  );
}

export const useModal: () => ModalContextData = () => {
  return useContext(ModalContext);
};

// Workflow

export type WorkflowView = "OVERVIEW" | "SOURCE" | "BLOCK" | "OUTPUTS" | "PREVIEW" | "SIGNATURE_REQUEST";
export interface WorkflowBlock {
  blockType: string;
  blockId: string;
}

export interface WorkflowContextData {
  workflow: Workflow;
  mutateWorkflow: KeyedMutator<Workflow>;
  updateAutoCreate: (value: boolean) => void;
  automationOptions: any;
  updateAutomationOptions: (options: any) => void;
  showAutomationPanel: boolean;
  setShowAutomationPanel: (f: boolean) => void;
}

export const WorkflowContext = createContext<WorkflowContextData>({
  workflow: {} as Workflow,
  mutateWorkflow: {} as KeyedMutator<Workflow>,
  updateAutoCreate: () => { },
  automationOptions: {},
  updateAutomationOptions: () => { },
  showAutomationPanel: false,
  setShowAutomationPanel: () => { },
});

interface WorkflowOnlyContextData {
  workflow?: Workflow;
}

export const WorkflowOnlyContext = createContext<WorkflowOnlyContextData>({});

// Backend

interface BackendContextData {
  version?: string | null;
}

export const BackendContext = createContext<BackendContextData>({});

interface LastFocusedEditorData {
  editorRef: RefObject<TemplatedTextEditorRef> | null;
  setEditorRef: Dispatch<SetStateAction<RefObject<any> | null>>;
};

export const LastFocusedEditorContext = createContext<LastFocusedEditorData>({
  editorRef: null,
  setEditorRef: () => { }
});

type WorkflowListView = 'ROWS' | 'CARDS';
type WorkflowFilter = 'ALL' | 'PERSONAL' | 'TEAM';

type WorkflowData = {
  workflowUrl: string;
  workflowFilter: WorkflowFilter;
  workflowSearch: string;
  workflowShowSearch: boolean;
  workflowListView: WorkflowListView;
}

type TemplateData = {
  templateCategory: string,
  templateUrl: string,
}

type SettingsData = {
  settingsUrl: string
}
interface SectionsData {
  workflow: WorkflowData;
  template: TemplateData;
  settings: SettingsData;
  updateWorkflowData: (property: keyof WorkflowData, value: string | boolean) => void;
  updateTemplateData: (property: keyof TemplateData, value: string) => void;
  updateSettingsData: (property: keyof SettingsData, value: string) => void;
}

export const SectionsContext = createContext<SectionsData>({
  workflow: {
    workflowUrl: '',
    workflowFilter: 'ALL',
    workflowListView: 'ROWS',
    workflowSearch: '',
    workflowShowSearch: false,
  },
  template: {
    templateCategory: '',
    templateUrl: '',
  },
  settings: {
    settingsUrl: ''
  },
  updateSettingsData: () => { },
  updateWorkflowData: () => { },
  updateTemplateData: () => { },
})

export function SectionsProvider(props: { children: React.ReactNode }) {
  const location = useLocation();
  const pathname = location.pathname;

  const [workflow, setWorkflow] = useState<WorkflowData>({
    workflowUrl: '',
    workflowFilter: 'ALL',
    workflowListView: localStorage.getItem("workflowListView") as WorkflowListView ?? "CARDS",
    workflowSearch: '',
    workflowShowSearch: false,
  });

  const [template, setTemplate] = useState<TemplateData>({
    templateCategory: '',
    templateUrl: '/templates',
  });

  const [settings, setSettings] = useState<SettingsData>({
    settingsUrl: '/settings',
  });

  const updateWorkflowData = (property: keyof WorkflowData, newValue: string | boolean) => {
    setWorkflow(prev => ({ ...prev, [property]: newValue }))
  }

  const updateTemplateData = (property: keyof TemplateData, newValue: string) => {
    setTemplate(prev => ({ ...prev, [property]: newValue }))
  }

  const updateSettingsData = (property: keyof SettingsData, newValue: string) => {
    setSettings(prev => ({ ...prev, [property]: newValue }))
  }

  useEffect(() => {
    localStorage.setItem("workflowListView", workflow.workflowListView);
  }, [workflow.workflowListView])

  useEffect(() => {
    if (pathname?.includes('settings')) {
      setSettings(prev => ({ ...prev, settingsUrl: pathname }));
    } else if (pathname?.includes('template')) {
      setTemplate(prev => ({ ...prev, templateUrl: pathname }));
    } else if (pathname === '/' || pathname?.includes('/w/')) {
      setWorkflow(prev => ({ ...prev, workflowUrl: pathname }));
    }
  }, [pathname]);

  return (
    <SectionsContext.Provider value={{
      workflow,
      template,
      settings,
      updateWorkflowData,
      updateTemplateData,
      updateSettingsData
    }}>
      <div className="relative">
        {props.children}
      </div>
    </SectionsContext.Provider>
  );
}
